import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Filter, SlidersHorizontal, Grid, List, ChevronDown, X, Zap } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SEO } from '../components/SEO';
import { useCurrency } from '../components/CurrencySelector';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Slider } from '../components/ui/slider';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { SearchFacets } from '../components/SearchFacets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ShopPage = () => {
  const { t, language, getLocalizedName } = useLanguage();
  const { format } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Filters
  const [priceRange, setPriceRange] = useState([0, 500]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [facetFilters, setFacetFilters] = useState({ priceRange: null, ratings: [], vendorIds: [] });
  
  const searchQuery = searchParams.get('search') || '';
  const categorySlug = searchParams.get('category') || '';
  const flashOnly = searchParams.get('flash') === '1';

  const toggleFlash = () => {
    const next = new URLSearchParams(searchParams);
    if (flashOnly) next.delete('flash'); else next.set('flash', '1');
    setSearchParams(next);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data);
        
        // Set initial category from URL
        if (categorySlug) {
          const cat = response.data.find(c => c.slug === categorySlug);
          if (cat) {
            setSelectedCategories([cat.id]);
          }
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, [categorySlug]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        
        if (searchQuery) params.append('search', searchQuery);
        if (selectedCategories.length === 1) params.append('category_id', selectedCategories[0]);
        if (priceRange[0] > 0) params.append('min_price', priceRange[0]);
        if (priceRange[1] < 500) params.append('max_price', priceRange[1]);
        if (flashOnly) params.append('flash_only', 'true');
        params.append('sort_by', sortBy);
        params.append('sort_order', sortOrder);
        params.append('limit', '24');
        
        const response = await axios.get(`${API}/products?${params.toString()}`);
        setProducts(response.data);
        setTotalProducts(response.data.length);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [searchQuery, selectedCategories, priceRange, sortBy, sortOrder, flashOnly]);

  const handleCategoryToggle = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      }
      return [categoryId]; // Single selection for now
    });
  };

  const clearFilters = () => {
    setPriceRange([0, 500]);
    setSelectedCategories([]);
    setSearchParams({});
  };

  const hasActiveFilters = selectedCategories.length > 0 || priceRange[0] > 0 || priceRange[1] < 500 || searchQuery;

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Categories */}
      <div>
        <h3 className="font-semibold mb-3">{t('categories')}</h3>
        <div className="space-y-2">
          {categories.map((category) => (
            <label key={category.id} className="flex items-center gap-3 cursor-pointer group">
              <Checkbox
                checked={selectedCategories.includes(category.id)}
                onCheckedChange={() => handleCategoryToggle(category.id)}
              />
              <span className="text-sm group-hover:text-[#0F4C5C] transition-colors">
                {getLocalizedName(category)}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="font-semibold mb-3">{t('price')}</h3>
        <div className="px-2">
          <Slider
            value={priceRange}
            onValueChange={setPriceRange}
            max={500}
            step={10}
            className="mb-4"
          />
          <div className="flex items-center justify-between text-sm">
            <span>{format(priceRange[0])}</span>
            <span>{format(priceRange[1])}</span>
          </div>
        </div>
      </div>

      {/* Clear Filters */}
      {hasActiveFilters && (
        <Button variant="outline" className="w-full" onClick={clearFilters}>
          <X className="w-4 h-4 mr-2" />
          {t('clear')} {t('filter')}
        </Button>
      )}
    </div>
  );

  const activeCategory = categories.find((c) => c.slug === categorySlug) || null;
  const catName = activeCategory ? (language === 'fr' ? activeCategory.name_fr : activeCategory.name_en) || activeCategory.name : null;

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: language === 'fr' ? 'Accueil' : 'Home', item: window.location.origin },
      { '@type': 'ListItem', position: 2, name: language === 'fr' ? 'Boutique' : 'Shop', item: `${window.location.origin}/shop` },
      ...(catName ? [{ '@type': 'ListItem', position: 3, name: catName, item: `${window.location.origin}/shop?category=${categorySlug}` }] : []),
    ],
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="shop-page">
      <SEO
        title={catName || (language === 'fr' ? 'Boutique' : 'Shop')}
        description={catName
          ? (language === 'fr'
              ? `Achetez ${catName} auprès de vendeurs vérifiés sur Respectful Markets — la marketplace responsable. Livraison rapide, paiement sécurisé.`
              : `Buy ${catName} from verified sellers on Respectful Markets — the conscious marketplace. Fast shipping, secure payment.`)
          : (language === 'fr'
              ? 'Découvrez des milliers de produits de marques, revendeurs et particuliers vérifiés. Livraison rapide, paiement sécurisé, avis clients.'
              : 'Discover thousands of products from verified brands, resellers and individuals. Fast shipping, secure payment, customer reviews.')}
        url={`${window.location.origin}/shop${categorySlug ? `?category=${categorySlug}` : ''}`}
        image={activeCategory?.image_url}
        jsonLd={breadcrumbLd}
      />
      {/* Header */}
      <div className="bg-white border-b border-border/40">
        <div className="container-main py-8">
          <h1 className="text-3xl font-bold mb-2">{t('shop')}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>{totalProducts} {language === 'fr' ? 'produits' : 'products'}</span>
            {searchQuery && (
              <>
                <span>•</span>
                <span>{language === 'fr' ? 'Recherche' : 'Search'}: "{searchQuery}"</span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container-main py-8">
        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0 space-y-4">
            <div className="sticky top-32 space-y-4">
              <div className="bg-white rounded-2xl border border-[#0F4C5C]/10 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold flex items-center gap-2 text-[#0F4C5C]">
                    <Filter className="w-4 h-4" />
                    {t('categories')}
                  </h2>
                </div>
                <FilterContent />
              </div>
              <SearchFacets
                categoryId={selectedCategories[0]}
                search={searchQuery}
                value={facetFilters}
                onChange={setFacetFilters}
              />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex flex-wrap items-center gap-2">
                {/* Flash Sales filter */}
                <Button
                  variant="outline"
                  onClick={toggleFlash}
                  className={`rounded-full ${flashOnly
                    ? 'bg-red-600 hover:bg-red-700 border-red-600 text-white hover:text-white'
                    : 'text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700'}`}
                  data-testid="flash-filter-btn"
                >
                  <Zap className={`w-4 h-4 mr-1.5 ${flashOnly ? 'fill-current' : ''}`} />
                  {language === 'fr' ? 'Ventes Flash' : 'Flash Sales'}
                  {flashOnly && <X className="w-3.5 h-3.5 ml-1.5" />}
                </Button>

              {/* Active Filters */}
              {hasActiveFilters && (
                <div className="flex flex-wrap items-center gap-2">
                  {selectedCategories.map(catId => {
                    const cat = categories.find(c => c.id === catId);
                    return cat ? (
                      <Badge key={catId} variant="secondary" className="flex items-center gap-1">
                        {getLocalizedName(cat)}
                        <X className="w-3 h-3 cursor-pointer" onClick={() => handleCategoryToggle(catId)} />
                      </Badge>
                    ) : null;
                  })}
                  {searchQuery && (
                    <Badge variant="secondary" className="flex items-center gap-1">
                      "{searchQuery}"
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearchParams({})} />
                    </Badge>
                  )}
                </div>
              )}
              </div>

              <div className="flex items-center gap-3 ml-auto">
                {/* Mobile Filter Button */}
                <Sheet>
                  <SheetTrigger asChild>
                    <Button variant="outline" className="lg:hidden">
                      <SlidersHorizontal className="w-4 h-4 mr-2" />
                      {t('filter')}
                    </Button>
                  </SheetTrigger>
                  <SheetContent side="left">
                    <SheetHeader>
                      <SheetTitle>{t('filter')}</SheetTitle>
                    </SheetHeader>
                    <div className="mt-6">
                      <FilterContent />
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Sort */}
                <Select value={`${sortBy}-${sortOrder}`} onValueChange={(value) => {
                  const [field, order] = value.split('-');
                  setSortBy(field);
                  setSortOrder(order);
                }}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={t('sort')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="created_at-desc">
                      {language === 'fr' ? 'Plus récent' : 'Newest'}
                    </SelectItem>
                    <SelectItem value="price-asc">
                      {language === 'fr' ? 'Prix croissant' : 'Price: Low to High'}
                    </SelectItem>
                    <SelectItem value="price-desc">
                      {language === 'fr' ? 'Prix décroissant' : 'Price: High to Low'}
                    </SelectItem>
                    <SelectItem value="rating-desc">
                      {language === 'fr' ? 'Mieux notés' : 'Top Rated'}
                    </SelectItem>
                  </SelectContent>
                </Select>

                {/* View Mode */}
                <div className="hidden sm:flex items-center border border-border rounded-lg">
                  <Button
                    variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                    size="icon"
                    className="rounded-r-none"
                    onClick={() => setViewMode('grid')}
                  >
                    <Grid className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                    size="icon"
                    className="rounded-l-none"
                    onClick={() => setViewMode('list')}
                  >
                    <List className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Products Grid */}
            {loading ? (
              <div className={viewMode === 'grid' ? 'product-grid' : 'space-y-4'}>
                {[...Array(12)].map((_, i) => (
                  <div key={i} className={viewMode === 'grid' ? 'space-y-4' : 'flex gap-4 bg-white p-4 rounded-xl'}>
                    <Skeleton className={viewMode === 'grid' ? 'aspect-square rounded-xl' : 'w-32 h-32 rounded-lg'} />
                    <div className="flex-1 space-y-2">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-24 h-24 mx-auto mb-6 bg-slate-100 rounded-full flex items-center justify-center">
                  <Filter className="w-10 h-10 text-muted-foreground" />
                </div>
                <h3 className="text-xl font-semibold mb-2">
                  {language === 'fr' ? 'Aucun produit trouvé' : 'No products found'}
                </h3>
                <p className="text-muted-foreground mb-6">
                  {language === 'fr' 
                    ? 'Essayez de modifier vos filtres'
                    : 'Try adjusting your filters'}
                </p>
                <Button onClick={clearFilters}>
                  {t('clear')} {t('filter')}
                </Button>
              </div>
            ) : (
              <div className={viewMode === 'grid' ? 'product-grid' : 'space-y-4'}>
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
